import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Calendar, Clock, CheckCircle2, Tag, Users, MapPin,
  ExternalLink, FilePlus, ImagePlus, FileText, ImageIcon,
  Trash2, Eye, ShieldCheck, AlertCircle, Plus, Camera, Archive
} from 'lucide-react';
import { pb, getFileUrl } from '../../lib/pb';
import { updateChantier, createChantier, deleteChantier } from '../../lib/chantiers';
import { fetchSettings } from '../../lib/settings';
import { calcBusinessHours, addBusinessHours, compressImage, pdfToImage } from '../../lib/utils';
import AddressAutocomplete from './AddressAutocomplete';
import RangeDatePicker from './RangeDatePicker';
import TravelIndicator from './TravelIndicator';

const PRESET_COLORS = [
  { name: 'Bleu', value: '#3B82F6' },
  { name: 'Émeraude', value: '#10B981' },
  { name: 'Ambre', value: '#F59E0B' },
  { name: 'Rose', value: '#F43F5E' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Orange', value: '#FB923C' },
  { name: 'Ardoise', value: '#64748B' },
  { name: 'Fuchsia', value: '#D946EF' },
  { name: 'Lime', value: '#84CC16' },
];

export default function ChantierModal({ item, onClose, onSaved }) {
  const [localItem, setLocalItem] = useState(item);
  const [formData, setFormData] = useState({
    title: item?.title || '',
    client_name: item?.client_name || '',
    location: item?.location || '',
    description: item?.description || '',
    status: item?.status || 'planned',
    budget_hours: item?.budget_hours ?? '',
    lon: item?.lon || null,
    color: item?.color || '#3B82F6'
  });
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('info');
  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [pendingFiles, setPendingFiles] = useState({ gallery: [], attachments: [] });

  const [workers, setWorkers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [assignForm, setAssignForm] = useState({ users: [], start_date: '', end_date: '', notes: '', hours_override: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const [closures, setClosures] = useState([]);
  const [editingHoursId, setEditingHoursId] = useState(null);
  const [editingHoursVal, setEditingHoursVal] = useState('');

  const loadTeam = useCallback(async () => {
    try {
      const [wk, cfg] = await Promise.all([
        pb.collection('users').getFullList({ filter: 'role = "worker" || role = "admin" || role = "commercial"', sort: 'name' }),
        fetchSettings()
      ]);
      setWorkers(wk);
      setSettings(cfg);
      if (cfg?.business_hours) setBusinessHours(cfg.business_hours);
      if (Array.isArray(cfg?.exceptional_closures)) setClosures(cfg.exceptional_closures);

      if (localItem?.id) {
        const as = await pb.collection('assignments').getFullList({ filter: `chantier = "${localItem.id}"`, expand: 'user', sort: 'start_date' });
        setAssignments(as);
      }
    } catch (e) { console.error(e); }
  }, [localItem?.id]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (assignForm.users.length === 0 || !assignForm.start_date) return;

    setAssignSaving(true);
    try {
      for (const userId of assignForm.users) {
        const newAs = {
          user: userId,
          chantier: localItem?.id || '',
          start_date: new Date(assignForm.start_date).toISOString(),
          end_date: assignForm.end_date ? new Date(assignForm.end_date).toISOString() : new Date(assignForm.start_date).toISOString(),
          notes: assignForm.notes,
          hours_override: assignForm.hours_override ? parseFloat(assignForm.hours_override) : null
        };

        if (localItem?.id) {
          await pb.collection('assignments').create(newAs);
        } else {
          const userObj = workers.find(w => w.id === userId);
          setPendingAssignments(prev => [...prev, { ...newAs, id: Date.now() + Math.random(), expand: { user: userObj } }]);
        }
      }
      setAssignForm({ users: [], start_date: '', end_date: '', notes: '', hours_override: '' });
      setShowAssignForm(false);
      if (localItem?.id) { loadTeam(); onSaved(); }
    } catch (err) { alert("Erreur d'assignation."); }
    finally { setAssignSaving(false); }
  };

  // --- SMART AUTO-CALC END DATE ---
  const ps = useRef(assignForm.start_date);
  const ph = useRef(assignForm.hours_override);
  useEffect(() => {
    const sc = ps.current !== assignForm.start_date;
    const hc = ph.current !== assignForm.hours_override;
    if ((sc || hc) && assignForm.start_date && assignForm.hours_override && businessHours) {
      const hours = parseFloat(assignForm.hours_override);
      if (!isNaN(hours) && hours > 0) {
        const newEnd = addBusinessHours(assignForm.start_date, hours, businessHours, closures);
        if (newEnd !== assignForm.end_date) {
          setAssignForm(prev => ({ ...prev, end_date: newEnd }));
        }
      }
    }
    ps.current = assignForm.start_date;
    ph.current = assignForm.hours_override;
  }, [assignForm.start_date, assignForm.hours_override, businessHours, closures]);

  const handleRemoveAssignment = async (id) => {
    if (!window.confirm('Retirer cet intervenant ?')) return;
    try { await pb.collection('assignments').delete(id); loadTeam(); onSaved(); }
    catch { alert('Erreur suppression.'); }
  };

  const handleSaveHoursOverride = async (assignmentId, val) => {
    try {
      await pb.collection('assignments').update(assignmentId, { hours_override: val === '' ? null : parseFloat(val) });
      setEditingHoursId(null); loadTeam();
    } catch (e) { console.error(e); }
  };

  const handleFileUpload = async (e, field) => {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;

    if (!localItem?.id) {
      // Preview mode for non-existent record
      let filesToStore = [];
      for (const f of files) {
        if (f.type === 'application/pdf') {
          const imgs = await pdfToImage(f);
          filesToStore.push(...(Array.isArray(imgs) ? imgs : [imgs]));
        } else {
          let finalFile = f;
          if (f.type.startsWith('image/')) finalFile = await compressImage(f);
          filesToStore.push(finalFile);
        }
      }
      setPendingFiles(prev => ({ ...prev, [field]: [...prev[field], ...filesToStore] }));
      return;
    }

    setIsSaving(true); setUploadError('');
    try {
      for (const f of files) {
        if (f.type === 'application/pdf') {
          const imgs = await pdfToImage(f);
          for (const img of (Array.isArray(imgs) ? imgs : [imgs])) {
            const fd = new FormData(); fd.append(field + '+', img);
            const updated = await pb.collection('chantiers').update(localItem.id, fd); setLocalItem(updated);
          }
        } else {
          let finalFile = f;
          if (f.type.startsWith('image/')) finalFile = await compressImage(f);
          const fd = new FormData(); fd.append(field + '+', finalFile);
          const updated = await pb.collection('chantiers').update(localItem.id, fd); setLocalItem(updated);
        }
      }
      onSaved();
    } catch (err) { setUploadError("Erreur upload."); } finally { setIsSaving(false); }
  };

  const handleRemoveFile = async (fileName, field) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append(field + '-', fileName);
      const updated = await pb.collection('chantiers').update(localItem.id, fd);
      setLocalItem(updated);
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMainSave = async (e) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    try {
      let chantierId = localItem?.id;
      if (localItem?.id) {
        await updateChantier(localItem.id, formData);
      } else {
        // Mode Création avec fichiers et assignations en attente
        const fd = new FormData();
        Object.keys(formData).forEach(k => { if (formData[k] != null) fd.append(k, formData[k]); });
        for (const f of pendingFiles.gallery) {
          const compressed = f.type.startsWith('image/') ? await compressImage(f) : f;
          fd.append('gallery', compressed);
        }
        for (const f of pendingFiles.attachments) {
          fd.append('attachments', f);
        }
        const created = await pb.collection('chantiers').create(fd);
        chantierId = created.id;
        for (const pa of pendingAssignments) {
          await pb.collection('assignments').create({ ...pa, chantier: chantierId });
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      alert('Erreur sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay-p" onClick={(e) => e.target.className === 'modal-overlay-p' && onClose()}>
      <div className="modal-content-p">
        <div className="modal-p-top">
          <div className="modal-p-tabs">
            <button className={tab === 'info' ? 'active' : ''} onClick={() => setTab('info')}>Information</button>
            <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Média</button>
            <button className={tab === 'team' ? 'active' : ''} onClick={() => setTab('team')}>Équipe {(!localItem && pendingAssignments.length > 0) ? `(${pendingAssignments.length})` : ''}</button>
          </div>
          <button className="p-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-p-body">
          {tab === 'info' ? (
            <div className="p-form">
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10, display: 'block' }}>État</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, background: '#f1f5f9', padding: 4, borderRadius: 16 }}>
                  {[
                    { id: 'planned', label: 'Planifié', color: '#eab308', bg: '#fef9c3', icon: <Calendar size={14} /> },
                    { id: 'in_progress', label: 'En Cours', color: '#0ea5e9', bg: '#e0f2fe', icon: <Clock size={14} /> },
                    { id: 'completed', label: 'Terminé', color: '#10b981', bg: '#dcfce7', icon: <CheckCircle2 size={14} /> },
                    { id: 'archived', label: 'Archivé', color: '#64748b', bg: '#f1f5f9', icon: <Archive size={14} /> }
                  ].map(s => {
                    const active = formData.status === s.id;
                    return (
                      <button key={s.id} type="button" onClick={() => setFormData({ ...formData, status: s.id })}
                        style={{ border: 'none', background: active ? s.bg : 'transparent', color: active ? s.color : '#64748b', padding: '10px 4px', borderRadius: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}>
                        {s.icon} <span style={{ fontSize: 11, fontWeight: 800 }}>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10, display: 'block' }}>Couleur Planning</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, background: '#f8fafc', padding: 8, borderRadius: 16, border: '1.5px solid #f1f5f9' }}>
                  {PRESET_COLORS.map(c => (
                    <button key={c.value} type="button" onClick={() => setFormData({ ...formData, color: c.value })}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', border: formData.color === c.value ? '2px solid #fff' : 'none',
                        boxShadow: formData.color === c.value ? `0 0 0 2px ${c.value}, 0 4px 6px -1px rgba(0,0,0,0.1)` : 'none',
                        background: c.value, cursor: 'pointer', transition: 'all 0.2s', transform: formData.color === c.value ? 'scale(1.15)' : 'scale(1)'
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>

              <div style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 24, padding: 20, display: 'grid', gap: 16 }}>
                <div className="p-field">
                  <label>Titre</label>
                  <div style={{ position: 'relative' }}>
                    <Tag size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1' }} />
                    <input required style={{ paddingLeft: 38 }} value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="ex: Rénovation..." />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="p-field">
                    <label>Client</label>
                    <div style={{ position: 'relative' }}>
                      <Users size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1' }} />
                      <input style={{ paddingLeft: 38 }} value={formData.client_name} onChange={e => setFormData({ ...formData, client_name: e.target.value })} placeholder="Client" />
                    </div>
                  </div>
                  <div className="p-field">
                    <label>ID</label>
                    <input readOnly value={localItem?.id ? `#${localItem.id.slice(0, 8)}` : 'Nouveau'} style={{ background: '#f8fafc', color: '#94a3b8', fontStyle: 'italic' }} />
                  </div>
                </div>
                <div className="p-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <label style={{ margin: 0 }}>Lieu</label>
                    {formData.location && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.location)}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, fontWeight: 800, color: '#0ea5e9', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}><ExternalLink size={10} /> Maps</a>}
                  </div>
                  <AddressAutocomplete
                    value={formData.location}
                    onChange={val => setFormData({ ...formData, location: val })}
                    onSelect={s => setFormData({ ...formData, location: s.label, lat: s.lat, lon: s.lon })}
                  />

                  {formData.lat && settings?.base_lat && (
                    <div style={{ marginTop: 12 }}>
                      <TravelIndicator
                        lat1={settings.base_lat} lon1={settings.base_lon}
                        lat2={formData.lat} lon2={formData.lon}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 24, padding: 20 }}>
                  <div className="p-field" style={{ margin: 0 }}><label><Clock size={12} /> Budget h</label>
                    <div style={{ position: 'relative' }}><input type="number" step="0.5" value={formData.budget_hours} onChange={e => setFormData({ ...formData, budget_hours: e.target.value })} style={{ paddingRight: 30 }} /><span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 800, color: '#94a3b8' }}>h</span></div>
                  </div>
                </div>
                <div style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 24, padding: 20 }}>
                  <div className="p-field" style={{ margin: 0 }}><label>Description</label><textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} style={{ fontSize: 12 }} /></div>
                </div>
              </div>
            </div>
          ) : tab === 'team' ? (() => {
              const allAs = [...pendingAssignments, ...assignments];
              const totalConsumed = allAs.reduce((sum, a) => {
                const autoH = calcBusinessHours(a.start_date, a.end_date, businessHours, closures);
                return sum + (a.hours_override != null ? parseFloat(a.hours_override) : autoH);
              }, 0);
              const budgetPerPers = parseFloat(formData.budget_hours) || 0;
              const numWorkersTotal = allAs.length || 0;
              const totalBudget = budgetPerPers * numWorkersTotal;
              const isOverBudget = totalConsumed > totalBudget && totalBudget > 0;

              return (
            <div style={{ display: 'grid', gap: 16 }}>
              {/* Budget Summation Bar */}
              <div style={{ background: isOverBudget ? '#fff1f2' : '#f0fdf4', border: '1px solid ' + (isOverBudget ? '#fecaca' : '#bbf7d0'), borderRadius: 16, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                   <div style={{ fontSize: 10, fontWeight: 900, color: isOverBudget ? '#e11d48' : '#16a34a', textTransform: 'uppercase', marginBottom: 2 }}>Main-d'œuvre ({numWorkersTotal} pers.)</div>
                   <div style={{ fontSize: 18, fontWeight: 900, color: isOverBudget ? '#be123c' : '#166534' }}>
                     {Math.round(totalConsumed * 10) / 10}h <span style={{ fontSize: 13, fontWeight: 700, opacity: 0.6 }}>/ {totalBudget > 0 ? (totalBudget + 'h') : '∞'}</span>
                   </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                   {totalBudget > 0 && (
                     <div style={{ width: 100, height: 8, background: isOverBudget ? '#fda4af' : '#dcfce7', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ position: 'absolute', left:0, top:0, bottom:0, width: Math.min(100, (totalConsumed/totalBudget)*100) + '%', background: isOverBudget ? '#e11d48' : '#22c55e' }} />
                     </div>
                   )}
                   <div style={{ fontSize: 10, fontWeight: 800, marginTop: 4, color: isOverBudget ? '#e11d48' : '#16a34a' }}>{isOverBudget ? 'Dépassement !' : 'Ratio respecté'}</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 900 }}>Assignations ({assignments.length})</h3>
                <button onClick={() => setShowAssignForm(v => !v)} style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 10, padding: '6px 12px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>{showAssignForm ? '✕ Annuler' : '+ Assigner'}</button>
              </div>
              {showAssignForm && (
                <form onSubmit={handleAssign} style={{ background: '#f8fafc', borderRadius: 16, padding: 15, border: '1px solid #e2e8f0', display: 'grid', gap: 12 }}>
                  <div className="p-field">
                    <label>Membre(s) / Intervenant(s)</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 6, background: '#fff', padding: 10, borderRadius: 16, border: '1.5px solid #e2e8f0', maxHeight: 150, overflowY: 'auto' }}>
                      {workers.map(w => {
                        const active = assignForm.users.includes(w.id);
                        return (
                          <button key={w.id} type="button" onClick={() => {
                            setAssignForm(prev => ({
                              ...prev,
                              users: active ? prev.users.filter(id => id !== w.id) : [...prev.users, w.id]
                            }));
                          }} style={{ border: '1.5px solid ' + (active ? '#0ea5e9' : '#f1f5f9'), background: active ? '#f0f9ff' : 'transparent', color: active ? '#0369a1' : '#64748b', padding: '6px 10px', borderRadius: 10, fontSize: 11, fontWeight: 800, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {active ? <CheckCircle2 size={12} /> : <Plus size={12} />}
                            {w.name || w.username}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="p-field">
                    <label>Période</label>
                    <RangeDatePicker
                      startDate={assignForm.start_date}
                      endDate={assignForm.end_date}
                      onChange={(s, e) => setAssignForm({ ...assignForm, start_date: s, end_date: e })}
                    />
                  </div>
                  <div className="p-field">
                    <label>Durée Travaux (h)</label>
                    <input
                      type="number" step="0.5" placeholder="Saisir heures pour calculer la fin..."
                      value={assignForm.hours_override}
                      onChange={e => setAssignForm({ ...assignForm, hours_override: e.target.value })}
                    />
                  </div>
                  <button type="submit" className="p-btn-save" disabled={assignSaving}>{assignSaving ? 'Assignation...' : '✓ Confirmer'}</button>
                </form>
              )}
              <div style={{ display: 'grid', gap: 10 }}>
                {([...pendingAssignments, ...assignments]).map(a => {
                  const w = a.expand?.user; const initials = (w?.name || w?.username || '?').slice(0, 2).toUpperCase();
                  const autoH = calcBusinessHours(a.start_date, a.end_date, businessHours, closures);
                  const effectiveH = a.hours_override != null ? a.hours_override : autoH;
                  const isEditing = editingHoursId === a.id; const isPending = !localItem?.id || pendingAssignments.some(pa => pa.id === a.id);
                  return (
                    <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '12px', display: 'flex', gap: 12, opacity: isPending ? 0.8 : 1 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: isPending ? '#eff6ff' : '#f0fdf4', border: '1px solid ' + (isPending ? '#bfdbfe' : '#bbf7d0'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: isPending ? '#3b82f6' : '#16a34a' }}>{initials}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 13 }}>{w?.name || w?.username}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>📅 {new Date(a.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → {Math.round(effectiveH * 10) / 10}h</div>
                      </div>
                      <button onClick={() => isPending ? setPendingAssignments(p => p.filter(x => x.id !== a.id)) : handleRemoveAssignment(a.id)} style={{ width: 30, height: 30, border: 'none', background: '#fff1f2', borderRadius: 8, color: '#f43f5e', cursor: 'pointer' }}><Trash2 size={13} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )
          })() : (
            <div className="p-media-area">
              <div style={{ display: 'grid', gap: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <label style={{ background: '#f0fdf4', border: '2px dashed #bbf7d0', borderRadius: 20, padding: 15, textAlign: 'center', cursor: 'pointer' }}><ImagePlus size={18} /><div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>Photos</div><input type="file" multiple accept="image/*" hidden onChange={e => handleFileUpload(e, 'gallery')} /></label>
                  <label style={{ background: '#f0f9ff', border: '2px dashed #b9e6fe', borderRadius: 20, padding: 15, textAlign: 'center', cursor: 'pointer' }}><FilePlus size={18} /><div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>PDF Plan</div><input type="file" multiple accept=".pdf" hidden onChange={e => handleFileUpload(e, 'attachments')} /></label>
                  <label style={{ background: '#fff7ed', border: '2px dashed #fed7aa', borderRadius: 20, padding: 15, textAlign: 'center', cursor: 'pointer' }}><Camera size={18} /><div style={{ fontSize: 10, fontWeight: 800, marginTop: 4 }}>Photo Plan</div><input type="file" accept="image/*" capture="environment" hidden onChange={e => handleFileUpload(e, 'attachments')} /></label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                  {/* Pending files */}
                  {pendingFiles.gallery.map((f, i) => (
                    <div key={'p-' + i} style={{ height: 100, borderRadius: 12, overflow: 'hidden', position: 'relative', border: '2px solid #22c55e' }}>
                      <img src={URL.createObjectURL(f)} alt="pending" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 10, background: 'rgba(0,0,0,0.2)' }}>EN ATTENTE</div>
                      <button onClick={() => setPendingFiles(prev => ({ ...prev, gallery: prev.gallery.filter((_, idx) => idx !== i) }))} style={{ position: 'absolute', top: 5, right: 5, background: '#fff', border: 'none', borderRadius: 6, width: 22, height: 22 }}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  {pendingFiles.attachments.map((f, i) => {
                    const isImage = f.type.startsWith('image/');
                    return (
                      <div key={'pa-' + i} style={{ height: 100, borderRadius: 12, background: '#f8fafc', border: '2px solid #0ea5e9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                        {isImage ? (
                          <img src={URL.createObjectURL(f)} alt="pending" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                        ) : (
                          <FileText size={20} color="#0ea5e9" />
                        )}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 9, background: 'rgba(0,0,0,0.2)' }}>EN ATTENTE</div>
                        <button onClick={() => setPendingFiles(prev => ({ ...prev, attachments: prev.attachments.filter((_, idx) => idx !== i) }))} style={{ position: 'absolute', top: 5, right: 5, background: '#fff', border: 'none', borderRadius: 6, width: 22, height: 22, zIndex: 10 }}><Trash2 size={12} /></button>
                      </div>
                    );
                  })}

                  {/* Existing files */}
                  {localItem?.id && (localItem.gallery || []).map(img => (
                    <div key={img} style={{ height: 100, borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
                      <img src={getFileUrl(localItem, img)} alt="site" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => handleRemoveFile(img, 'gallery')} style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: 6, width: 22, height: 22 }}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  {localItem?.id && (localItem.attachments || []).map(file => {
                    const isImage = file.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                    return (
                      <div key={file} style={{ height: 100, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                        {isImage ? (
                          <img src={getFileUrl(localItem, file)} alt="plan" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <>
                            <FileText size={20} color="#64748b" />
                            <div style={{ fontSize: 8, fontWeight: 800, textAlign: 'center', marginTop: 5, wordBreak: 'break-all', padding: '0 5px' }}>{file}</div>
                          </>
                        )}
                        <button onClick={() => handleRemoveFile(file, 'attachments')} style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: 6, width: 22, height: 22, zIndex: 10 }}><Trash2 size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-p-footer">
          <button type="button" className="p-btn-save" style={{ flex: 1 }} onClick={handleMainSave} disabled={isSaving}>
            {isSaving ? 'Enregistrement...' : <><ShieldCheck size={18} /> Enregistrer</>}
          </button>
          {localItem?.id && (
            <button type="button" className="p-btn-delete" onClick={async () => { if (window.confirm('Supprimer ce chantier définitivement ?')) { await deleteChantier(localItem.id); onSaved(); onClose(); } }}>
              <Trash2 size={20} />
            </button>
          )}
        </div>
        <style>{`
          .modal-overlay-p { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(5px); z-index: 25000; display: flex; align-items: center; justify-content: center; padding: 20px; }
          .modal-content-p { background: #fff; width: 100%; max-width: 580px; border-radius: 28px; overflow: hidden; max-height: 90vh; display: flex; flex-direction: column; }
          .modal-p-top { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; }
          .modal-p-tabs { display: flex; gap: 4px; background: #f1f5f9; padding: 3px; border-radius: 10px; }
          .modal-p-tabs button { border: none; background: none; padding: 6px 14px; border-radius: 8px; font-weight: 800; font-size: 12px; color: #64748b; cursor: pointer; }
          .modal-p-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
          .p-close { width: 32px; height: 32px; border-radius: 50%; border: none; background: #f8fafc; }
          .modal-p-body { padding: 20px; overflow-y: auto; flex: 1; }
          .modal-p-footer { padding: 15px 20px; background: #fff; border-top: 1px solid #f1f5f9; display: flex; gap: 12px; }
          .p-btn-delete { width: 50px; background: #fff1f2; color: #f43f5e; border: 1.5px solid #fee2e2; border-radius: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
          .p-btn-delete:hover { background: #fee2e2; }
          .p-form { display: grid; gap: 15px; }
          .p-field { display: grid; gap: 4px; }
          .p-field label { font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; }
          .p-field input, .p-field select, .p-field textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; padding: 10px 14px; border-radius: 12px; font-weight: 700; color: #0f172a; outline: none; }
          .p-btn-save { background: #0f172a; color: #fff; border: none; padding: 12px; border-radius: 12px; font-weight: 800; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s; }
          .p-btn-save:hover { opacity: 0.9; transform: translateY(-1px); }
          @media (max-width: 768px) { .modal-content-p { height: 100vh; max-height: 100vh; border-radius: 0; } }
        `}</style>
      </div>
    </div>
  );
}
